import { observer } from 'mobx-react';
import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import { CircularProgress, IconButton, Typography } from '@mui/material';

import { SEARCH_VIEW_COOKIE } from 'app/constants';
import SearchStore from 'app/store/SearchStore';
import useStores from 'hooks/use-stores';
import EmptyBox from 'main/components/panel/EmptyBox';
import Speller from 'main/components/search/Speller';
import { SearchType } from 'main/components/SearchBar';
import { ElasticQueryBuilder } from 'main/modules/elastic';
import Occurrences from 'pages/Search/components/Database/components/Occurrences';
import SearchResultsListItem from 'pages/Search/components/Database/components/SearchResultsListItem';
import SearchResultsTable from 'pages/Search/components/Database/components/SearchResultsTable';
import UpgradeLicense from 'pages/Search/components/Database/components/UpgradeLicense';
import useFormattedData from 'pages/Search/components/Database/hooks/use-formatted-data';
import Paginator from 'pages/Search/components/Paginator';
import { getCookie, setCookie } from 'utils/cookies';
import isBrowser from 'utils/is-browser';
import isEmpty from 'utils/is-empty';
import { stringifyQuery } from 'utils/stringify-query';

import useStyles from './styles';

interface Props {
    onPageChange: (parameters: object) => void;
}

const SearchResults: React.FC<Props> = ({ onPageChange }) => {
    const location = useLocation();
    const { classes, cx } = useStyles();
    const { searchStore } = useStores();

    useMemo(() => {
        if (isBrowser()) {
            const view = getCookie(SEARCH_VIEW_COOKIE);
            if (view) {
                searchStore.setView(view);
            }
        }
    }, [searchStore]);

    const skip = parseInt(new URLSearchParams(location.search).get('skip') || '') || 0;
    const data = useFormattedData({ items: searchStore.data, exact: searchStore.exactMatch });

    const getPaginationHref = useCallback(
        (page) =>
            stringifyQuery('/search', {
                query: searchStore.query,
                software: searchStore.software,
                version: searchStore.version,
                state: ElasticQueryBuilder.stateToString(searchStore.requestState),
                skip: (page - 1) * searchStore.pageSize || '',
                size: searchStore.pageSize === SearchStore.DEFAULT_PAGE_SIZE ? '' : searchStore.pageSize
            }),
        [searchStore.pageSize, searchStore.query, searchStore.requestState, searchStore.software, searchStore.version]
    );

    const handlePaginationChange = useCallback(
        ({ skip, size }) => {
            const query = new URLSearchParams(window.location.search).get('query')?.trim() || '';
            const software = new URLSearchParams(window.location.search).get('software')?.trim() || '';
            const version = new URLSearchParams(window.location.search).get('version')?.trim() || '';
            const state = new URLSearchParams(window.location.search).get('state')?.trim() || '';

            onPageChange({
                query,
                software,
                version,
                state: ElasticQueryBuilder.stringToState(state),
                skip: skip || '',
                size: size === SearchStore.DEFAULT_PAGE_SIZE ? '' : size
            });
        },
        [onPageChange]
    );

    const handlePageChange = useCallback(
        (page) => {
            handlePaginationChange({ skip: (page - 1) * searchStore.pageSize, size: searchStore.pageSize });
        },
        [handlePaginationChange, searchStore.pageSize]
    );

    const handleRowsPerPageChange = useCallback(
        (rowsPerPage) => {
            handlePaginationChange({ skip: 0, size: rowsPerPage });
        },
        [handlePaginationChange]
    );

    const handleSpellerClick = useCallback((query) => onPageChange({ query, software: '', version: '', skip: 0 }), [
        onPageChange
    ]);

    if (searchStore.isLoading) {
        return (
            <EmptyBox>
                <CircularProgress size={64} thickness={2} />
            </EmptyBox>
        );
    }

    if (isEmpty(data)) {
        if (searchStore.type === SearchType.product && !searchStore.productSearchEnabled) {
            return <UpgradeLicense />;
        }
        return (
            <>
                <Speller
                    show={searchStore.type === SearchType.simple}
                    query={searchStore.lastSearchQuery}
                    onSpellClick={handleSpellerClick}
                />
                <EmptyBox>
                    <Typography className={classes.notFound}>{'Nothing found'}</Typography>
                </EmptyBox>
            </>
        );
    }

    return (
        <>
            <div className={classes.header}>
                <p className={classes.total}>{`${searchStore.total} results`}</p>
                <div className={classes.buttons}>
                    <IconButton
                        className={cx(classes.button, {
                            [classes.active]: searchStore.view === SearchStore.VIEWS.LIST
                        })}
                        onClick={() => {
                            searchStore.setView(SearchStore.VIEWS.LIST);
                            setCookie(SEARCH_VIEW_COOKIE, SearchStore.VIEWS.LIST, {
                                ['max-age']: Number.MAX_SAFE_INTEGER
                            });
                        }}
                    >
                        <TableRowsOutlinedIcon />
                    </IconButton>
                    <IconButton
                        className={cx(classes.button, {
                            [classes.active]: searchStore.view === SearchStore.VIEWS.TABLE
                        })}
                        onClick={() => {
                            searchStore.setView(SearchStore.VIEWS.TABLE);
                            setCookie(SEARCH_VIEW_COOKIE, SearchStore.VIEWS.TABLE, {
                                ['max-age']: Number.MAX_SAFE_INTEGER
                            });
                        }}
                    >
                        <MenuOutlinedIcon />
                    </IconButton>
                </div>
            </div>

            {isEmpty(searchStore.occurrences) ? null : (
                <Occurrences data={searchStore.occurrences} className={classes.occurrences} />
            )}

            {searchStore.view === SearchStore.VIEWS.LIST ? (
                <div className={classes.list}>
                    {data.map((data, index) => (
                        <SearchResultsListItem key={index} data={data} />
                    ))}
                </div>
            ) : null}

            {searchStore.view === SearchStore.VIEWS.TABLE ? (
                <SearchResultsTable data={data} className={classes.table} />
            ) : null}

            <Paginator
                className={classes.paginator}
                page={Math.floor(skip / searchStore.pageSize) + 1}
                siblingCount={1}
                rowsPerPage={searchStore.pageSize}
                count={searchStore.total}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                getPaginationHref={getPaginationHref}
            />
        </>
    );
};

export default observer(SearchResults);
